import axios from "axios";
import mixpanel from "mixpanel-browser";

// GO-3538: Hydra answers 401 for a dead token, but also for some reads a live
// token just isn't allowed to make, e.g. GET /orders/{href} for a downline
// member's order (API-12837 asks Hydra for a 403 there). So a 401 alone can't
// end the session. Before logging out, ask /whoami, which only answers 401
// when the token itself is no good.

// Bounds the check so a hung /whoami can't leave a request pending forever.
const WHOAMI_TIMEOUT_MS = 10000;

// A "still valid" answer is reused this long, so 401s that land just after the
// check settles don't each make a new call. A "dead" answer is kept for as long
// as that token is the one in use, so a token is only ever logged out once.
const VALID_ANSWER_REUSE_MS = 10000;

let lastCheck = null;

// Only a 401 from /whoami means the token is dead. A network error, timeout or
// 5xx says nothing about the session, so it keeps the user logged in. If the
// token really is dead, their next credentialed request checks again.
// `whoamiStatus` is what /whoami answered: its HTTP status, "timeout" or
// "network". Anything but 200 means the session wasn't actually confirmed.
const checkWhoami = (token) =>
  axios({
    method: "get",
    url: `${process.env.API_URL}/whoami`,
    headers: {
      Authorization: `Bearer ${token}`,
      "x-application": "office.web",
    },
    timeout: WHOAMI_TIMEOUT_MS,
  }).then(
    (res) => ({ dead: false, whoamiStatus: res?.status ?? 200 }),
    (err) => ({
      dead: err?.response?.status === 401,
      whoamiStatus:
        err?.response?.status ?? (err?.code === "ECONNABORTED" ? "timeout" : "network"),
    })
  );

// Path segments that name one resource rather than a kind of resource: Hydra
// hrefs (hex hashes), numeric ids (unicityIds, order numbers) and UUIDs.
const ID_SEGMENT = /^(\d+|[0-9a-f]{16,}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

// One key per endpoint, whichever call path reported it: no query string, no
// Hydra prefix for callers that passed a full URL, office-api calls under
// "office-api/", and ids collapsed to ":id" so a breakdown by endpoint gives
// one row per route, not one per order or member (GO-3547).
const endpointKey = (endpoint) => {
  let path = String(endpoint ?? "").split("?")[0];
  const hydraPrefix = `${process.env.API_URL}/`;
  const officeApiPrefix = `${process.env.REACT_APP_OFFICE_API}/`;
  if (path.startsWith(hydraPrefix)) {
    path = path.slice(hydraPrefix.length);
  } else if (process.env.REACT_APP_OFFICE_API && path.startsWith(officeApiPrefix)) {
    path = `office-api/${path.slice(officeApiPrefix.length)}`;
  }
  return path
    .split("/")
    .map((segment) => (ID_SEGMENT.test(segment) ? ":id" : segment))
    .join("/");
};

// Hydra sends the header twice ("id, id"); keep one copy.
const requestIdOf = (error) => {
  const id = error?.response?.headers?.["x-request-id"];
  return id ? String(id).split(",")[0].trim() : undefined;
};

/**
 * Decides what a 401 on a credentialed request means. Resolves true when the
 * session is dead (logout() has run, once per token), false when the session is
 * still valid and the 401 was about that one request. 401s that arrive while a
 * check is in flight share it, so a burst makes one /whoami call. Each 401 it
 * lets through is tracked as api_401_session_still_valid (GO-3547), with
 * `whoami_status` saying whether /whoami actually confirmed the session.
 * Never rejects.
 */
export const checkSessionAfter401 = ({ token, logout, error, endpoint, method }) => {
  const reusable =
    lastCheck !== null &&
    lastCheck.token === token &&
    (lastCheck.validAt === null ||
      Date.now() - lastCheck.validAt < VALID_ANSWER_REUSE_MS);

  if (!reusable) {
    const check = { token, validAt: null };
    // No token means there's no session to keep.
    check.result = (
      token ? checkWhoami(token) : Promise.resolve({ dead: true, whoamiStatus: null })
    ).then((answer) => {
      if (answer.dead) {
        // Callers await this inside their error handling, so it must never
        // reject: a throw here would leave their request unsettled.
        try {
          logout();
        } catch (logoutError) {
          console.error(logoutError);
        }
      } else {
        check.validAt = Date.now();
      }
      return answer;
    });
    lastCheck = check;
  }

  return lastCheck.result.then(({ dead, whoamiStatus }) => {
    if (!dead) {
      try {
        mixpanel.track("api_401_session_still_valid", {
          endpoint: endpointKey(endpoint),
          method: method?.toLowerCase(),
          status: error?.response?.status,
          x_request_id: requestIdOf(error),
          whoami_status: whoamiStatus,
        });
      } catch (trackError) {
        console.error(trackError);
      }
    }
    return dead;
  });
};
